import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button, Table } from "react-bootstrap";
import { 
  fetchAlpacaAccount, 
  fetchAlpacaPositions, 
  fetchAlpacaOrders,
  getCacheAge,
  clearAllCaches
} from "./service/AlpacaService";
import PerformanceCards from "./components/TradeDashboard/PerformanceCards";
import EquityCurveChart from "./components/TradeDashboard/EquityCurveChart";

const TradeDashboard = () => {
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load data on component mount
  useEffect(() => {
    loadAlpacaData();
  }, []);

  const loadAlpacaData = async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Fetch all data in parallel
      const [accountData, positionsData, ordersData] = await Promise.all([
        fetchAlpacaAccount(forceRefresh),
        fetchAlpacaPositions(forceRefresh),
        fetchAlpacaOrders({}, forceRefresh)
      ]);

      setAccount(accountData);
      setPositions(positionsData);
      setOrders(ordersData);
      
      // Update last updated timestamp
      setLastUpdated(new Date());
      
      setLoading(false);
      setRefreshing(false);
    } catch (err) {
      console.error("Error loading Alpaca data:", err);
      setError("Failed to load account data");
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Mask account number - show only last 4 digits
  const maskAccountNumber = (accountNum) => {
    if (!accountNum) return "****";
    const numStr = accountNum.toString();
    if (numStr.length <= 4) return numStr;
    return "****" + numStr.slice(-4);
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value) => {
    if (!value && value !== 0) return "0.00%";
    return (value * 100).toFixed(2) + "%";
  };

  // Group orders by date
  const groupOrdersByDate = () => {
    const grouped = {};
    
    orders.forEach(order => {
      if (!order.filled_at) return;
      
      const date = new Date(order.filled_at).toISOString().split('T')[0];
      
      if (!grouped[date]) {
        grouped[date] = {
          date: date,
          orders: [],
          totalPnL: 0,
          trades: 0
        };
      }
      
      grouped[date].orders.push(order);
      grouped[date].trades += 1;
      
      // Calculate P&L if available
      if (order.filled_avg_price && order.qty) {
        // This is simplified - you may need more complex P&L calculation
        // based on entry/exit prices for round-trip trades
      }
    });

    // Convert to array and sort by date descending
    return Object.values(grouped).sort((a, b) => 
      new Date(b.date) - new Date(a.date)
    );
  };

  // Handle row click to navigate to detailed day view
  const handleRowClick = (dateGroup) => {
    // Navigate to Dashboard with date parameter for detailed intraday analysis
    window.location.href = `/DailyDetail?date=${dateGroup.date}`;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Format time for display
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Format last updated time (e.g., "2m ago", "Just now")
  const formatLastUpdated = () => {
    if (!lastUpdated) return 'Never';
    
    const seconds = Math.floor((new Date() - lastUpdated) / 1000);
    
    if (seconds < 10) return 'Just now';
    if (seconds < 60) return `${seconds}s ago`;
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: '#000000', minHeight: '100vh', paddingTop: '2rem' }}>
        <Container>
          <h3 style={{ textAlign: 'center', color: '#e0e0e0' }}>Loading Alpaca data...</h3>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: '#000000', minHeight: '100vh', paddingTop: '2rem' }}>
        <Container>
          <div style={{ 
            backgroundColor: '#1a1a1a', 
            border: '1px solid #ef4444', 
            padding: '1rem', 
            borderRadius: '4px',
            color: '#ef4444'
          }}>
            <h4>Error</h4>
            <p>{error}</p>
            <Button variant="secondary" onClick={() => loadAlpacaData(true)}>
              Retry
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  const dailySummary = groupOrdersByDate();

  return (
    <div style={{ backgroundColor: '#000000', minHeight: '100vh' }}>
      {/* Connection Header */}
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        padding: '12px 20px',
        marginBottom: '0'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col xs={12} md={6}>
              <span style={{ color: '#10b981', fontSize: '18px', marginRight: '8px' }}>●</span>
              <span style={{ color: '#e0e0e0', fontSize: '14px' }}>
                Connected to Alpaca Paper Trading
              </span>
              {account && (
                <span style={{ color: '#888', fontSize: '14px', marginLeft: '20px' }}>
                  Account: {maskAccountNumber(account.account_number)}
                </span>
              )}
            </Col>
            <Col xs={12} md={6} className="text-end mt-2 mt-md-0">
              <button
                onClick={() => loadAlpacaData(true)}
                disabled={refreshing}
                style={{
                  background: refreshing ? '#2a2a2a' : 'transparent',
                  border: '1px solid #333',
                  color: refreshing ? '#666' : '#888',
                  padding: '6px 14px',
                  borderRadius: '4px',
                  cursor: refreshing ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  marginRight: '12px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!refreshing) {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.color = '#10b981';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!refreshing) {
                    e.target.style.borderColor = '#333';
                    e.target.style.color = '#888';
                  }
                }}
              >
                {refreshing ? '⟳ Refreshing...' : '🔄 Refresh'}
              </button>
              <span style={{ color: '#666', fontSize: '12px' }}>
                Updated: {formatLastUpdated()}
              </span>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Demo Mode Banner */}
      <div style={{
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        padding: '10px 20px',
        marginBottom: '0'
      }}>
        <Container>
          <span style={{ fontSize: '16px', marginRight: '8px' }}>ðŸŽ­</span>
          <span style={{ color: '#f59e0b', fontSize: '14px' }}>
            DEMO MODE - Viewing sample trading account data
          </span>
        </Container>
      </div>

      <Container style={{ paddingTop: '2rem' }}>
        <br />
        <h1 style={{ color: '#e0e0e0' }}>Alpaca Dashboard</h1>

        {/* Account Summary */}
        {account && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <h3 style={{ color: '#e0e0e0', marginBottom: '20px' }}>Account Summary</h3>
            <Row>
              <Col md={3}>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ color: '#888', fontSize: '12px', marginBottom: '5px' }}>
                    EQUITY
                  </div>
                  <div style={{ color: '#e0e0e0', fontSize: '24px', fontWeight: '600' }}>
                    {formatCurrency(account.equity)}
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ color: '#888', fontSize: '12px', marginBottom: '5px' }}>
                    BUYING POWER
                  </div>
                  <div style={{ color: '#e0e0e0', fontSize: '24px', fontWeight: '600' }}>
                    {formatCurrency(account.buying_power)}
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ color: '#888', fontSize: '12px', marginBottom: '5px' }}>
                    CASH
                  </div>
                  <div style={{ color: '#e0e0e0', fontSize: '24px', fontWeight: '600' }}>
                    {formatCurrency(account.cash)}
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ color: '#888', fontSize: '12px', marginBottom: '5px' }}>
                    STATUS
                  </div>
                  <div style={{ 
                    color: account.status === 'ACTIVE' ? '#10b981' : '#f59e0b', 
                    fontSize: '18px', 
                    fontWeight: '600' 
                  }}>
                    {account.status}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Performance Cards */}
        {account && positions && orders && (
          <PerformanceCards 
            account={account} 
            positions={positions} 
            orders={orders} 
          />
        )}

        {/* Equity Curve Chart */}
        {account && (
          <EquityCurveChart />
        )}

        {/* Current Positions */}
        {positions && positions.length > 0 && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <h3 style={{ color: '#e0e0e0', marginBottom: '20px' }}>Current Positions</h3>
            <Table striped bordered hover variant="dark" size="sm">
              <thead>
                <tr style={{ backgroundColor: '#0a0a0a' }}>
                  <th>Symbol</th>
                  <th>Qty</th>
                  <th>Entry Price</th>
                  <th>Current Price</th>
                  <th>Market Value</th>
                  <th>P&L</th>
                  <th>P&L %</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((pos, idx) => {
                  const unrealizedPL = parseFloat(pos.unrealized_pl || 0);
                  const unrealizedPLPercent = parseFloat(pos.unrealized_plpc || 0);
                  const isProfit = unrealizedPL >= 0;

                  return (
                    <tr key={idx}>
                      <td style={{ fontWeight: '600' }}>{pos.symbol}</td>
                      <td>{pos.qty}</td>
                      <td>{formatCurrency(pos.avg_entry_price)}</td>
                      <td>{formatCurrency(pos.current_price)}</td>
                      <td>{formatCurrency(pos.market_value)}</td>
                      <td style={{ color: isProfit ? '#10b981' : '#ef4444' }}>
                        {formatCurrency(unrealizedPL)}
                      </td>
                      <td style={{ color: isProfit ? '#10b981' : '#ef4444' }}>
                        {formatPercent(unrealizedPLPercent)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        {/* Daily Summary Table */}
        {dailySummary.length > 0 && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <h3 style={{ color: '#e0e0e0', marginBottom: '20px' }}>Trading Activity by Day</h3>
            <p style={{ color: '#888', fontSize: '14px', marginBottom: '15px' }}>
              Click on a row to view detailed intraday analysis
            </p>
            <Table striped bordered hover variant="dark" size="sm" style={{ cursor: 'pointer' }}>
              <thead>
                <tr style={{ backgroundColor: '#0a0a0a' }}>
                  <th style={{ width: '35%' }}>Date</th>
                  <th style={{ width: '55%' }}>Trades</th>
                  <th style={{ width: '10%', textAlign: 'center', padding: '8px' }}></th>
                </tr>
              </thead>
              <tbody>
                {dailySummary.map((day, idx) => (
                  <tr 
                    key={idx} 
                    onClick={() => handleRowClick(day)}
                    style={{ 
                      backgroundColor: 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td>{formatDate(day.date)}</td>
                    <td>{day.trades}</td>
                    <td style={{ textAlign: 'center', padding: '8px' }}>
                      <span style={{ color: '#10b981', fontSize: '18px', fontWeight: '600' }}>›</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* No Orders Message */}
        {orders.length === 0 && !loading && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '40px',
            marginTop: '20px',
            textAlign: 'center'
          }}>
            <h4 style={{ color: '#888' }}>No trading activity found</h4>
            <p style={{ color: '#666' }}>Orders will appear here once trades are executed</p>
          </div>
        )}
      </Container>
      <br />
    </div>
  );
};

export default TradeDashboard;